/* ---------------------------------------------------------------------------
   LWC - the storefront corrections that cannot be made in CSS.

   Everything here is additive and defensive: if any of it fails the page is
   exactly the theme's own behaviour. Nothing is removed from the DOM.

   12 August 2026. Two jobs moved out of this file and into Liquid, because
   doing them here meant the internal facet was in the served HTML and only
   disappeared once JavaScript ran - crawlers, no-JS sessions and the moment
   before hydration all saw it:
     - hiding the internal "Dispatch Speed" (custom.warehouse) facet
     - hiding facets with a single value
   Both are now skipped in snippets/facets.liquid.

   The MutationObserver on #facets also went. component-facets.js replaces
   #product-grid-ajax, #products-count-ajax, #current-facets-ajax and
   #pagination-ajax on a filter change; it never touches the drawer's facet
   list, so the observer never fired for the thing it was written to protect,
   and every attribute this file set retriggered it.

   13 August 2026, after the Taiga 3.4.0 -> 8.1.0 upgrade. The price filter was
   rebuilt upstream. 3.4.0 rendered a <price-range> element wrapping a
   noUiSlider, with .price-range__input-min / .price-range__input-max inputs and
   .noUi-handle handles. 8.1.0 renders <price-range-slider> with .thumb handles
   and input.price-range-input[data-min] / [data-max]. Every selector in the two
   price functions below matched nothing after the upgrade - they did not error,
   they just silently did nothing, which is why this needed hunting rather than
   reporting. Both now read the 8.1.0 markup and still tolerate the old.

   14 August 2026. Three changes.

   1. The drawer dialog work here is gone. role="dialog", aria-modal and the
      accessible name are now in snippets/drawer-menu.liquid,
      snippets/drawer-filters.liquid and snippets/drawer-tabs.liquid, so they
      are correct in the served HTML instead of appearing once script has run.
      drawer-search.liquid already had them. Only #drawer-cart is still handled
      here, because snippets/drawer-cart.liquid is not ours to edit; the guard
      is written so that whoever does edit it will not be clobbered.
      The 120ms timer that applied them on open, and the focus-return handler,
      both went with it. Focus return was measured and the theme already does
      it: global.js remembers the opener in showModalOrDrawer and
      removeTrapFocus puts focus back on close and on Escape.

   2. Applying a filter replaces four containers and says nothing. A shopper
      using a screen reader had no way to know 190 products had become 154.
      There is now one polite status region, and it moves inside whichever
      drawer is open before it speaks, because an aria-modal dialog hides the
      rest of the document from assistive technology.

   3. The same replacement can throw focus to <body>. Ticking a checkbox does
      not, because the facet forms sit outside all four replaced containers,
      but removing a filter from the active-filter row does: that control lives
      inside #current-facets-ajax and is destroyed by its own click. Focus is
      now put back on the equivalent control in the new markup, or on the
      results count, which is the sentence a shopper wants at that moment.

   19 August 2026. Two changes, both about the desktop facet rail.

   1. Everything in run() was scoped to `getElementById('facets') || document`.
      Only snippets/drawer-filters.liquid emits <div id="facets">; the desktop
      rail is <form class="sidebar-facets"> inside .product-grid-sidebar and
      carries no id at all, so the type-to-filter boxes, the option counts and
      the slider labelling reached the drawer and never the rail - two thirds of
      traffic is phones, which is why it went unnoticed. run() now walks both
      containers. addFacetSearch already refused to add a second box to a group
      that has one, so running over an overlapping root cannot double up.

   2. component-facets.js now replaces those two containers on a filter change,
      so this file has to survive it. The facet containers are in AJAX_TARGETS,
      which is what makes the existing focus restoration cover a checkbox that
      is destroyed by its own click; and 'lwc:facets-replaced', which that file
      dispatches once the new markup is in, re-runs the enhancements. The text a
      shopper typed into a search box is remembered by group title and put back,
      because the box it was typed into no longer exists.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  function facetTitle(group) {
    var s = group.querySelector('summary span');
    return s ? (s.textContent || '').trim() : '';
  }

  /* Long facet lists.
     Brand on the big collections is a flat list of about 100 checkboxes with no
     way to get to a name except scrolling. A type-to-filter box turns that into
     two seconds of work. Purely additive: the checkboxes, their names and their
     values are untouched, so the form still submits exactly what it did before,
     and anything typed here is never sent. */
  var LONG_LIST = 12;

  /* What the shopper typed, by group title. The box is destroyed every time the
     facet markup is replaced, and losing a half-typed brand name each time a
     checkbox is ticked would be worse than not having the box at all. */
  var facetQueries = {};

  function addFacetSearch(root) {
    var groups = root.querySelectorAll('details.filter-group');
    for (var i = 0; i < groups.length; i++) {
      var g = groups[i];
      if (g.querySelector('[data-lwc-facet-search]')) continue;
      var list = g.querySelector('ul');
      if (!list) continue;
      var opts = list.querySelectorAll('.filter-option');
      if (opts.length < LONG_LIST) continue;

      var name = facetTitle(g) || 'options';
      var wrap = document.createElement('div');
      wrap.className = 'lwc-facet-search';
      var input = document.createElement('input');
      input.type = 'search';
      input.setAttribute('data-lwc-facet-search', '');
      input.setAttribute('autocomplete', 'off');
      input.placeholder = 'Search ' + name.toLowerCase();
      input.setAttribute('aria-label', 'Search within ' + name);
      var status = document.createElement('span');
      status.className = 'lwc-facet-search__n';
      status.setAttribute('role', 'status');
      status.textContent = opts.length + ' options';
      wrap.appendChild(input);
      wrap.appendChild(status);
      list.parentNode.insertBefore(wrap, list);
      list.classList.add('lwc-facet-list');

      (function (input, list, status, key) {
        function apply() {
          var q = input.value.trim().toLowerCase();
          var rows = list.querySelectorAll('.filter-option');
          var shown = 0;
          for (var j = 0; j < rows.length; j++) {
            var label = (rows[j].textContent || '').trim().toLowerCase();
            var hit = q === '' || label.indexOf(q) !== -1;
            rows[j].style.display = hit ? '' : 'none';
            if (hit) shown++;
          }
          status.textContent = q === '' ? rows.length + ' options' : shown + ' of ' + rows.length;
        }
        input.addEventListener('input', function () {
          facetQueries[key] = input.value;
          apply();
        });
        if (facetQueries[key]) {
          input.value = facetQueries[key];
          apply();
        }
      })(input, list, status, name);
    }
  }

  /* Price number fields. 8.1.0 gives each one a real <label for>, so this only
     acts where a template renders the field without one. An aria-label would
     otherwise override a perfectly good visible label. */
  function nameInputs(root) {
    var fields = root.querySelectorAll(
      'input.price-range-input, .price-range__input-min, .price-range__input-max'
    );
    for (var i = 0; i < fields.length; i++) {
      var f = fields[i];
      if (f.getAttribute('aria-label')) continue;
      var id = f.getAttribute('id');
      if (id) {
        var esc = id.replace(/"/g, '\\"');
        if (document.querySelector('label[for="' + esc + '"]')) continue;
      }
      if (f.closest('label')) continue;
      var isMax = f.hasAttribute('data-max') ||
                  f.className.indexOf('input-max') !== -1 ||
                  (f.getAttribute('name') || '').indexOf('lte') !== -1;
      f.setAttribute('aria-label', isMax ? 'Maximum price' : 'Minimum price');
    }
  }

  /* The price slider handles are focusable divs. 8.1.0 gives them role="slider"
     and tabindex, but no name and no values, so a screen reader announces two
     unlabelled sliders reading nothing. Give them names and live values read
     from the inputs they drive, and keep the values current as they move. */
  function isMaxThumb(t) {
    return t.hasAttribute('data-thumb-max') || (t.className || '').indexOf('max') !== -1;
  }

  function syncThumb(slider, thumb, min, max) {
    var isMax = isMaxThumb(thumb);
    var field = slider.querySelector(isMax ? 'input[data-max]' : 'input[data-min]') ||
                slider.querySelector(isMax ? '.price-range__input-max' : '.price-range__input-min');
    var now = field && field.value !== '' ? field.value : (isMax ? max : min);
    thumb.setAttribute('aria-valuenow', now);
    thumb.setAttribute('aria-valuetext', now + ' dollars');
  }

  function nameSliderHandles(root) {
    var sliders = root.querySelectorAll('price-range-slider, price-range');
    for (var i = 0; i < sliders.length; i++) {
      var s = sliders[i];
      var thumbs = s.querySelectorAll('.thumb, .noUi-handle, [class*="handle"]');
      if (!thumbs.length) continue;
      var min = s.getAttribute('data-min') || '0';
      var max = s.getAttribute('data-max') || '0';

      for (var h = 0; h < thumbs.length; h++) {
        var t = thumbs[h];
        var isMax = isMaxThumb(t);
        t.setAttribute('role', 'slider');
        if (!t.getAttribute('tabindex')) t.setAttribute('tabindex', '0');
        t.setAttribute('aria-valuemin', min);
        t.setAttribute('aria-valuemax', max);
        if (!t.getAttribute('aria-label')) {
          t.setAttribute('aria-label', isMax ? 'Maximum price' : 'Minimum price');
        }
        syncThumb(s, t, min, max);
      }

      if (!s.hasAttribute('data-lwc-slider-watched')) {
        s.setAttribute('data-lwc-slider-watched', '');
        (function (slider, lo, hi) {
          var update = function () {
            var list = slider.querySelectorAll('.thumb, .noUi-handle, [class*="handle"]');
            for (var k = 0; k < list.length; k++) syncThumb(slider, list[k], lo, hi);
          };
          slider.addEventListener('input', update);
          slider.addEventListener('change', update);
          slider.addEventListener('pointermove', update);
          slider.addEventListener('pointerup', update);
          slider.addEventListener('keyup', update);
        })(s, min, max);
      }
    }
  }

  /* The cart drawer. The other three drawers carry their dialog semantics in
     Liquid now; snippets/drawer-cart.liquid is not ours, so this fills the gap
     without overwriting anything that is already correct. */
  function fixCartDrawer() {
    var d = document.getElementById('drawer-cart');
    if (!d) return;
    var panel = d.querySelector('.drawer-panel');
    if (!panel) return;
    if (!panel.getAttribute('role')) panel.setAttribute('role', 'dialog');
    if (!panel.getAttribute('aria-modal')) panel.setAttribute('aria-modal', 'true');
    if (!panel.getAttribute('aria-label') && !panel.getAttribute('aria-labelledby')) {
      panel.setAttribute('aria-label', 'Shopping cart');
    }
    if (!panel.getAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
  }

  /* Top level navigation. These are <button> elements carrying role="link",
     which promises a page change and then opens a panel in place instead, and
     with no expanded state at all. The markup is in snippets/shortcut-menu.liquid,
     which is not ours to edit, so the semantics are corrected here and the
     expanded state is kept in step with the drawer the buttons drive. Nothing
     in component-drawer-menu.js reads role or aria-label; it dispatches on
     data-drawer-submenu-open, which is left alone. */
  function shortcutButtons() {
    return document.querySelectorAll('.header-shortlinks [data-drawer-submenu-open]');
  }

  function fixShortcutMenu() {
    var btns = shortcutButtons();
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      if (b.tagName !== 'BUTTON') continue;
      if (b.getAttribute('role') === 'link') b.removeAttribute('role');

      var id = b.getAttribute('data-drawer-submenu-open') || '';
      var panel = id ? document.getElementById(id + '-container') : null;
      if (panel) b.setAttribute('aria-controls', panel.id);

      /* "Navigate to Wine" on a control that does not navigate. The visible
         text is already the right name, so drop the override, but only when
         the visible text is actually there to fall back on. */
      var text = (b.textContent || '').trim();
      var label = b.getAttribute('aria-label') || '';
      if (text && label && label.indexOf(text) !== -1) b.removeAttribute('aria-label');

      if (!b.getAttribute('aria-expanded')) b.setAttribute('aria-expanded', 'false');
    }
    syncShortcutExpanded();
  }

  function syncShortcutExpanded() {
    var menu = document.getElementById('drawer-menu');
    var drawerOpen = !!(menu && menu.classList.contains('is-open'));
    var btns = shortcutButtons();
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      if (b.tagName !== 'BUTTON') continue;
      var id = b.getAttribute('data-drawer-submenu-open') || '';
      var acc = null;
      if (menu && id) {
        acc = menu.querySelector('[data-accordion-item][data-submenu-id="' + id.replace(/"/g, '\\"') + '"]');
      }
      var open = drawerOpen && acc && acc.classList.contains('is-open');
      b.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  }

  function watchShortcutMenu() {
    var menu = document.getElementById('drawer-menu');
    if (!menu || typeof MutationObserver === 'undefined') return;
    var pending = false;
    var mo = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      window.setTimeout(function () {
        pending = false;
        try { syncShortcutExpanded(); } catch (e) {}
      }, 60);
    });
    mo.observe(menu, { attributes: true, attributeFilter: ['class'], subtree: true });
  }

  /* ---------------------------------------------------------------------
     Filtering: the four containers component-facets.js replaces.
     --------------------------------------------------------------------- */
  var AJAX_TARGETS = [
    '#product-grid-ajax',
    '#products-count-ajax',
    '#current-facets-ajax',
    '#pagination-ajax',
    /* The two facet containers, which component-facets.js also replaces now.
       They are last so the four above keep their precedence in ajaxContainerOf.
       A checkbox that is destroyed by its own click is found again here by name
       and value, which is stable across the swap. */
    'form.sidebar-facets',
    '#facets'
  ];

  var liveRegion = null;
  var lastSpoken = '';
  var lastFocus = null;

  function getLiveRegion() {
    if (liveRegion && document.body && document.body.contains(liveRegion)) return liveRegion;
    liveRegion = document.getElementById('lwc-results-status');
    if (!liveRegion) {
      liveRegion = document.createElement('p');
      liveRegion.id = 'lwc-results-status';
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      document.body.appendChild(liveRegion);
    }
    return liveRegion;
  }

  /* An open drawer is aria-modal, which hides the rest of the document from a
     screen reader, so the region has to be inside it to be heard. */
  function liveRegionHome() {
    var drawers = document.querySelectorAll('.drawer.is-open, .modal.is-open');
    for (var i = 0; i < drawers.length; i++) {
      var panel = drawers[i].querySelector('[aria-modal="true"]') || drawers[i].querySelector('.drawer-panel');
      if (panel) return panel;
    }
    return document.body;
  }

  function countText() {
    var c = document.getElementById('products-count-ajax');
    var t = c ? (c.textContent || '').replace(/\s+/g, ' ').trim() : '';
    if (!t) {
      var a = document.getElementById('apply-count');
      t = a ? (a.textContent || '').replace(/\s+/g, ' ').trim() : '';
    }
    return t;
  }

  function announceCount() {
    var text = countText();
    if (!text || text === lastSpoken) return;
    lastSpoken = text;
    var region = getLiveRegion();
    var home = liveRegionHome();
    if (region.parentNode !== home) home.appendChild(region);
    region.textContent = '';
    window.setTimeout(function () {
      try { region.textContent = text; } catch (e) {}
    }, 60);
  }

  function ajaxContainerOf(el) {
    for (var i = 0; i < AJAX_TARGETS.length; i++) {
      if (el.closest(AJAX_TARGETS[i])) return AJAX_TARGETS[i];
    }
    return null;
  }

  function rememberFocus(e) {
    var t = e.target;
    if (!t || !t.closest || t === document.body) return;
    lastFocus = {
      el: t,
      sel: ajaxContainerOf(t),
      id: t.id || '',
      name: t.getAttribute ? (t.getAttribute('name') || '') : '',
      value: t.getAttribute ? (t.getAttribute('value') || '') : '',
      href: t.getAttribute ? (t.getAttribute('href') || '') : '',
      fp: t.getAttribute ? (t.getAttribute('data-filter-param-name') || '') : ''
    };
  }

  function q(s) { return String(s).replace(/"/g, '\\"'); }

  function tryFocus(el) {
    if (!el) return false;
    try {
      if (!el.getAttribute('tabindex') && !/^(A|BUTTON|INPUT|SELECT|TEXTAREA|SUMMARY)$/.test(el.tagName)) {
        el.setAttribute('tabindex', '-1');
      }
      el.focus({ preventScroll: false });
    } catch (err) {
      try { el.focus(); } catch (err2) { return false; }
    }
    return document.activeElement === el;
  }

  /* Returns true when focus was parked on the results count, so the count is
     read by the focus move and does not need saying twice. */
  function restoreFocusAfterReplace() {
    var a = document.activeElement;
    if (a && a !== document.body && a !== document.documentElement) return false;

    var L = lastFocus;
    if (!L || !L.sel) return false;
    if (L.el && document.contains(L.el)) return false;
    lastFocus = null;

    var root = document.querySelector(L.sel);
    if (!root) return false;

    var target = null;
    if (L.id) {
      var byId = document.getElementById(L.id);
      if (byId && root.contains(byId)) target = byId;
    }
    if (!target && L.name) {
      target = root.querySelector('[name="' + q(L.name) + '"]' + (L.value ? '[value="' + q(L.value) + '"]' : ''));
    }
    if (!target && L.href) {
      target = root.querySelector('a[href="' + q(L.href) + '"]');
    }
    if (!target && L.fp) {
      /* The control that was focused removed itself. Anything left in the same
         row is a better landing place than the top of the document. */
      target = root.querySelector('a[data-filter-param-name], a, button');
    }
    if (target && tryFocus(target)) return false;

    var count = document.getElementById('products-count-ajax');
    if (count && tryFocus(count)) return true;
    return tryFocus(root) ? true : false;
  }

  function watchResults() {
    if (typeof MutationObserver === 'undefined') return;
    var found = false;
    var pending = false;
    var mo = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      window.setTimeout(function () {
        pending = false;
        var parked = false;
        try { parked = restoreFocusAfterReplace(); } catch (e) {}
        try { if (!parked) announceCount(); } catch (e) {}
      }, 150);
    });
    for (var i = 0; i < AJAX_TARGETS.length; i++) {
      var el = document.querySelector(AJAX_TARGETS[i]);
      if (!el) continue;
      found = true;
      mo.observe(el, { childList: true, subtree: true, characterData: true });
    }
    if (found) {
      lastSpoken = countText();
      document.addEventListener('focusin', rememberFocus, true);
    }
  }

  /* The decade links on the homepage point at a product search for the ten
     years in that decade, which works with no app and no configuration. The
     search page then headlines the raw query. Give it a sentence instead. */
  function tidySearchHeading() {
    if (!document.documentElement.className.match(/template-search/)) return;
    var h1 = document.querySelector('main h1') || document.querySelector('h1');
    if (!h1) return;
    var m = (h1.textContent || '').match(/(\d{4})(?:\s+OR\s+\d{4}){5,}/);
    if (!m) return;
    var start = parseInt(m[1], 10);
    if (isNaN(start)) return;
    h1.textContent = 'Bottles from the ' + start + 's';
  }

  /* Both facet containers: the drawer's <div id="facets"> and the desktop rail's
     <form class="sidebar-facets">. Falls back to the whole document so search and
     any other template that renders its facets differently still gets the work. */
  function facetRoots() {
    var found = document.querySelectorAll('#facets, form.sidebar-facets');
    var roots = [];
    for (var i = 0; i < found.length; i++) {
      var el = found[i];
      var covered = false;
      for (var j = 0; j < roots.length; j++) {
        if (roots[j].contains(el)) { covered = true; break; }
      }
      if (!covered) roots.push(el);
    }
    return roots.length ? roots : [document];
  }

  /* ---------------------------------------------------------------------
     P5, P1, P2 and P3. Keyboard faults in markup that is not ours to edit.
     --------------------------------------------------------------------- */

  /* P5. Three drawer close controls render as <a role="button" href="#">:
     "Close navigation" twice and "Close cart drawer". Because they announce as
     buttons a screen-reader user presses Space, and a native anchor ignores
     Space, so the conclusion is that the drawer cannot be closed. Changing the
     element would mean touching the drawer JavaScript, so the key is handled
     here instead. Enter already works and is left alone. */
  function spaceActivatesButtonAnchors() {
    document.addEventListener('keydown', function (e) {
      if (e.key !== ' ' && e.key !== 'Spacebar') return;
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.isContentEditable) return;
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      var a = t.closest('a[role="button"]');
      if (!a) return;
      e.preventDefault();
      a.click();
    });
  }

  /* P1 and P2. Route's own widget markup, not ours and identical on the live
     theme, so neither is a regression: a closed modal marked aria-hidden that
     still holds three focusable links, and three empty h3 elements that corrupt
     the cart page heading outline. Both raised with Route. Until Route fixes
     them this keeps the closed modal out of the tab order.

     SET AND UNSET, and this is the part that matters. When Route opens the
     modal it stops being aria-hidden, and an element left inert would be dead
     to keyboard and pointer alike. Route is live and selling, so this must
     never be able to disable a modal that is meant to be open. Nothing else
     about Route is touched. */
  function routeModals() {
    if (typeof HTMLElement === 'undefined' || !('inert' in HTMLElement.prototype)) return;
    var nodes = document.querySelectorAll('[class*="route-modal"]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var closed = el.getAttribute('aria-hidden') === 'true';
      if (el.inert !== closed) el.inert = closed;
    }
  }

  /* P3. Judge.me's carousel arrows and histogram rows carry tabindex="0" with
     no role and no name, so they are tab stops that announce nothing and do
     nothing. Identical on the live theme. Judge.me exposes no setting for the
     template, so the tabindex is removed here. The elements themselves and
     their click handlers are untouched. */
  var JDGM_DEAD_TAB_STOPS =
    '.jdgm-carousel__left-arrow, .jdgm-carousel__right-arrow, .jdgm-histogram__row';

  function judgemeTabStops() {
    var nodes = document.querySelectorAll(JDGM_DEAD_TAB_STOPS);
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].getAttribute('tabindex') !== null) nodes[i].removeAttribute('tabindex');
    }
  }

  /* Both widgets inject after load and re-render on their own schedule, so one
     pass at init is not enough. Debounced the same way as the other observers
     in this file, because this one is on the document root: without the 200ms
     gate every attribute either function sets would retrigger it. aria-hidden
     is in the filter so a modal that opens or closes is caught; the inert
     attribute is deliberately not, so setting it cannot feed back. */
  function watchThirdParty() {
    routeModals();
    judgemeTabStops();
    if (typeof MutationObserver === 'undefined') return;
    var pending = false;
    var mo = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      window.setTimeout(function () {
        pending = false;
        try { routeModals(); } catch (e) {}
        try { judgemeTabStops(); } catch (e) {}
      }, 200);
    });
    mo.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'tabindex', 'class']
    });
  }

  function run() {
    var roots = facetRoots();
    for (var i = 0; i < roots.length; i++) {
      try { addFacetSearch(roots[i]); } catch (e) {}
      try { nameInputs(roots[i]); } catch (e) {}
      try { nameSliderHandles(roots[i]); } catch (e) {}
    }
    try { fixCartDrawer(); } catch (e) {}
    try { fixShortcutMenu(); } catch (e) {}
  }

  function init() {
    run();
    try { tidySearchHeading(); } catch (e) {}
    try { watchShortcutMenu(); } catch (e) {}
    try { watchResults(); } catch (e) {}
    try { spaceActivatesButtonAnchors(); } catch (e) {}
    try { watchThirdParty(); } catch (e) {}
  }

  /* component-facets.js replaces the two facet containers on a filter change and
     says so once the new markup is in place. Everything run() does to them has
     to be done again. */
  document.addEventListener('lwc:facets-replaced', function () {
    try { run(); } catch (e) {}
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  /* One more pass after load, for the price slider, whose handles
     component-price-range.js positions asynchronously. */
  window.addEventListener('load', run);
})();
